import { PALETTE } from '@overlayqa/shared';
import { useState } from 'react';
import type { TypeBreakdown, TypeBreakdownByRange, IssueTypeValue } from '@overlayqa/shared';
import { ISSUE_TYPES } from '@overlayqa/shared';
import { HugeiconsIcon } from '@hugeicons/react';
import { EmptyState } from '../ui/EmptyState';
import { ChartColumnIcon } from '@hugeicons/core-free-icons';
import { SelectDropdown } from '../shared/SelectDropdown';
import { Tooltip } from '../shared/Tooltip';
import { getTypeVisual, IconifyGlyph } from '../shared/badges/TypeBadge';
import { StatBars } from './StatBars';

type RangeKey = keyof TypeBreakdownByRange;

interface IssueTypeBarsProps {
  data: TypeBreakdown;
  /** Compact horizontal rows inside the dashboard's shared overview card. */
  variant?: 'default' | 'overview';
  /**
   * When provided, the card header renders a time-range filter and the chart
   * shows the selected range's data instead of `data` in the standalone card.
   * The dashboard overview always uses the all-time data.
   */
  byRange?: TypeBreakdownByRange;
}

// Bright -chart- palette, not the darkened badge-text tokens. See
// SeverityDonut and badgeContrast.source.test.ts for why the two split.
// Keys + labels come from the shared vocabulary (2026-09-02), so a type can
// no longer be silently dropped from the chart by a four-entry literal.
const CHART_COLOR: Record<IssueTypeValue, string> = {
  'design-bug': 'var(--color-type-chart-bug)',
  'design-gap': 'var(--color-type-chart-gap)',
  'design-debt': 'var(--color-type-chart-debt)',
  accessibility: 'var(--color-type-chart-a11y)',
  'design-token': 'var(--color-type-chart-token)',
  improvement: 'var(--color-type-chart-improvement)',
  general: 'var(--color-type-chart-general)',
};
const TYPE_CONFIG = ISSUE_TYPES.map((t) => ({
  key: t.value,
  label: t.shortLabel,
  color: CHART_COLOR[t.value],
  pickable: t.pickable,
}));

const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: 'week', label: 'Last week' },
  { value: 'month', label: 'Last month' },
  { value: 'quarter', label: 'Last 3 months' },
];

/** The shared glyph for a type (TypeBadge owns which glyph that is), at 13px:
 *  20% under the card header's 16px icon (Emily, 2026-09-22), rounded to a
 *  whole pixel so the stroke stays crisp. The word travels as the wrapping
 *  span's accessible name and its hover tooltip, never as text. */
function LabelGlyph({ type }: { type: IssueTypeValue }) {
  const visual = getTypeVisual(type);
  if (!visual) return null;
  if (visual.icon) {
    return <HugeiconsIcon icon={visual.icon} size={13} strokeWidth={2} color="currentColor" className="w-[13px] h-[13px]" aria-hidden="true" />;
  }
  if (visual.iconifyIcon) return <IconifyGlyph name={visual.iconifyIcon} className="w-[13px] h-[13px]" />;
  return null;
}

/** Issue types as a vertical bar chart (columns). The shared type glyph sits
 *  under each column (Emily, 2026-09-21: icons, never words, so seven columns
 *  fit any card width); a light grid gives the heights a reference. */
export function IssueTypeBars({ data, byRange, variant = 'default' }: IssueTypeBarsProps) {
  const [range, setRange] = useState<RangeKey>('week');
  const displayData = byRange && variant === 'default' ? byRange[range] : data;

  // Coerce each count to a finite number so a NaN total can't slip past the
  // `total === 0` empty-state guard.
  const countOf = (key: (typeof TYPE_CONFIG)[number]['key']): number => {
    const n = Number(displayData[key]);
    return Number.isFinite(n) ? n : 0;
  };
  const maxValue = Math.max(...TYPE_CONFIG.map(({ key }) => countOf(key)), 1);
  const total = TYPE_CONFIG.reduce((sum, { key }) => sum + countOf(key), 0);
  // The four human types are always columns; a scanner-stamped type (Debt,
  // A11y, Token) is a column only once it has issues, so a team that never
  // ran that audit is not shown three empty bars.
  const columns = TYPE_CONFIG.filter(({ key, pickable }) => pickable || countOf(key) > 0);

  if (variant === 'overview') {
    const rows = columns.map(({ key, label, color }) => ({ key, label, value: countOf(key), color, icon: <span style={{ color }}><LabelGlyph type={key} /></span> }))
      .sort((a, b) => b.value - a.value);
    return <section id="issue-types" aria-label="Issue types" className="min-w-0 flex flex-col">
      <h2 className="text-sm font-semibold text-gray-700">Issue types</h2>
      <p className="mt-2 mb-6 text-xs text-gray-500">All time · most common first</p>
      {total === 0
        ? <EmptyState size="sm" className="flex-1" icon={ChartColumnIcon} title="No issues yet" />
        : <StatBars variant="compact" rows={rows} className="gap-4" />}
    </section>;
  }

  return (
    <section id="issue-types" aria-label="Issue types" className="card-sm h-full flex flex-col min-w-0">
      <h2 className="section-label mb-3 flex items-center justify-between">
        Issue Types
        <HugeiconsIcon icon={ChartColumnIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-300" aria-hidden="true" />
      </h2>
      {byRange && (
        <div className="flex justify-end py-4">
          <SelectDropdown
            variant="ghost"
            value={range}
            options={RANGE_OPTIONS}
            onChange={(v) => setRange(v as RangeKey)}
            aria-label="Filter issue types by time range"
          />
        </div>
      )}
      {total === 0 ? (
        <EmptyState size="sm" className="flex-1" icon={ChartColumnIcon} title={byRange ? 'No issues in this period' : 'No issues yet'} />
      ) : (
        <div className="flex flex-col flex-1 pt-3">
          {/* Plot: light horizontal gridlines give the bar heights a reference;
              the count floats just above each bar (artifact layout).
              Radius is 4px, not the 2px this plot carried before 2026-08-06: 2
              is off the reduced radius scale (3/4/5/6/8/12 + full) and reds
              styles/__tests__/radiusScale.source.test.ts. 4 is the nearest step
              on the scale. */}
          <div
            data-label-plot
            className="flex-1 min-h-[92px] rounded"
            style={{
              // gray-100 gridlines every 32px; grays are Tailwind, not CSS vars.
              backgroundImage:
                `repeating-linear-gradient(to top, transparent 0, transparent 31px, ${PALETTE.neutral[100]} 31px, ${PALETTE.neutral[100]} 32px)`,
            }}
          >
            <div className="flex items-end gap-4 h-full px-1">
              {columns.map(({ key, label, color }) => {
                const value = countOf(key);
                const pct = (value / maxValue) * 100;
                return (
                  <div key={key} className="flex-1 h-full flex flex-col items-center justify-end gap-2">
                    <span className="text-xs font-semibold text-gray-700 tabular-nums leading-none">{value}</span>
                    <div
                      // role="img" so the aria-label is permitted: a plain div
                      // has the generic role, which forbids aria-label
                      // (axe aria-prohibited-attr, flagged dogfooding 2026-07-14).
                      role="img"
                      className="w-full max-w-[42px] rounded-t transition-all"
                      style={{ height: `${value > 0 ? Math.max(pct, 4) : 0}%`, backgroundColor: color }}
                      aria-label={`${label}: ${value} issues`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4 mt-2 px-1" data-label-row>
            {/* The shared bubble names the type on hover; no native title, or
                the browser's own tooltip would stack on the same hover. */}
            {columns.map(({ key, label }) => (
              <Tooltip key={key} label={label} placement="top">
                <span role="img" aria-label={label} className="flex-1 flex justify-center text-gray-500">
                  <LabelGlyph type={key} />
                </span>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function IssueTypeBarsSkeleton({ variant = 'default' }: { variant?: 'default' | 'overview' }) {
  if (variant === 'overview') return <section aria-label="Issue types loading" className="min-w-0">
    <div className="h-3 w-24 rounded skeleton-shimmer" />
    <div className="h-2.5 w-40 mt-3 mb-6 rounded skeleton-shimmer" />
    <div className="space-y-4">{TYPE_CONFIG.filter(type => type.pickable).map(type => <div key={type.key} className="flex items-center gap-4"><div className="h-3 w-[105px] rounded skeleton-shimmer" /><div className="h-1.5 flex-1 rounded skeleton-shimmer" /><div className="h-3 w-5 rounded skeleton-shimmer" /></div>)}</div>
  </section>;
  return (
    <section aria-label="Issue types loading" className="card-sm h-full flex flex-col min-w-0">
      <div className="h-3 rounded w-20 mb-3 skeleton-shimmer" />
      <div className="flex items-end gap-4 flex-1 pt-4 pb-2">
        {[80, 45, 65, 30].map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
            <div className="w-full rounded-t skeleton-shimmer" style={{ height: `${h}%` }} />
            <div className="h-3 rounded w-8 skeleton-shimmer" />
          </div>
        ))}
      </div>
    </section>
  );
}
